import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import PblCaseList from '@/components/Pbl/CaseList';
import type { PblCaseListColumn } from '@/components/Pbl/CaseList/types';
import PATH from '@/router/path';
import { errorHandler } from '@/utils/toastHandler';

import { mapPblCaseToOcPendingReviewCase } from './caseReviewState';
import { loadOcCasePages } from './loadCasePages';
import type { PblOcPendingReviewCase } from './types';
import { useTranslation } from 'react-i18next';

const buildPendingDetailPath = (caseId: string) => PATH.pbl.pendingReviewDetail.replace(':caseId', caseId);

const PblOcPendingReviewList = () => {
  //   @ts-ignore
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [rows, setRows] = useState<PblOcPendingReviewCase[]>([]);
  const [totalEntries, setTotalEntries] = useState(0);
  const columns = useMemo<PblCaseListColumn[]>(
    () => [
      { key: 'applicantName', label: t('pbl.pagesHardcoded.ocManageCases.pendingReviewList.applicantName') },
      { key: 'legalServiceNeeds', label: t('pbl.pagesHardcoded.ocManageCases.pendingReviewList.legalServiceNeeds') },
      { key: 'creationDate', label: t('pbl.pagesHardcoded.ocManageCases.pendingReviewList.creationDate') },
      { key: 'status', label: t('pbl.pagesHardcoded.ocManageCases.pendingReviewList.status') },
    ],
    [t]
  );

  useEffect(() => {
    let isMounted = true;

    const loadCases = async () => {
      try {
        const cases = await loadOcCasePages('pending_oc_review');

        if (!isMounted) return;

        setRows(cases.map(mapPblCaseToOcPendingReviewCase));
        setTotalEntries(cases.length);
      } catch (error: any) {
        if (isMounted) {
          errorHandler(
            error.response?.data?.message || error.response?.data?.data || t('pbl.pagesHardcoded.ocManageCases.pendingReviewList.failedToLoadPendingReviewCases')
          );
        }
      }
    };

    loadCases();

    return () => {
      isMounted = false;
    };
  }, []);

  const goToDetail = (item: PblOcPendingReviewCase) => {
    navigate(buildPendingDetailPath(String(item.id)));
  };

  return (
    <PblCaseList
      title={t('pbl.pagesHardcoded.ocManageCases.pendingReviewList.proBonoLegalServiceCasesReview')}
      rows={rows}
      columns={columns}
      primaryAction={{
        label: t('pbl.pagesHardcoded.ocManageCases.pendingReviewList.currentCaseValue', { value1: totalEntries }),
        onClick: () => navigate(PATH.pbl.cases),
      }}
      totalEntries={totalEntries}
      backTo={PATH.pbl.cases}
      searchAriaLabel="Search pending review cases"
      onRowClick={goToDetail}
    />
  );
};

export default PblOcPendingReviewList;
